/**
 * ProviderDocumentShareDialog
 * ===========================
 * TREATX-PROVIDER-DOCUMENT-SHARING-12A
 *
 * Clinic-side equivalent of SecureShareDialog. Lets a clinic user
 * (provider_admin / sales_rep / coordinator / doctor / after_sales_rep)
 * upload and share a document into the current canonical thread in one
 * coherent step.
 *
 * Canonical guarantees enforced HERE (mirrors patient-side):
 *  - Recipient (patient) is auto-resolved from the active thread.
 *  - The clinic user never sees patient_id; the dialog displays a
 *    patient-readable banner.
 *  - A deliberate consent click + category selection is required.
 *  - Posts multipart to /api/clinic/thread-attachments (the SOLE
 *    canonical clinic-share endpoint — same `attachment_access` /
 *    `attachment_access_logs` / `medical_attachments` collections).
 *  - No parallel sharing path.
 *
 * Props:
 *  - open: bool
 *  - onClose: () => void
 *  - onShared: ({ count, attachment_ids }) => void
 *  - threadId: string                          (auto-resolved)
 *  - recipientLabel: string                    (patient-readable name)
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ShieldCheck, X, FileText, FileImage, Loader2, AlertCircle, Upload, User } from 'lucide-react';
import { Button } from './ui/button';
import { SafeHtml } from './security/SafeHtml';
import { toast } from 'sonner';
import api from '../utils/api';

export const PROVIDER_DOC_SHARE_JOURNEY = 'messaging_provider_document_share';

// Category VALUES are backend identifiers and are never translated; only
// their labels are resolved through the canonical i18n mechanism.
const CATEGORY_VALUES = [
  'brochure', 'physician_cv', 'accreditation', 'preparation', 'post_op',
  'visa_guidance', 'travel_instructions', 'quote_pdf', 'illustration', 'other',
];

const MAX_FILES = 5;
const MAX_TOTAL_MB = 25;

const FilePreview = ({ file, onRemove }) => {
  const isImage = (file.type || '').startsWith('image/');
  return (
    <div
      className="flex items-center gap-2 px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs"
      data-testid={`provider-share-file-${file.name}`}
    >
      {isImage ? (
        <FileImage className="h-3.5 w-3.5 text-blue-500" />
      ) : (
        <FileText className="h-3.5 w-3.5 text-red-500" />
      )}
      <span className="truncate max-w-[180px]">{file.name}</span>
      <span className="text-slate-400">{(file.size / 1024).toFixed(0)} KB</span>
      {onRemove && (
        <button
          type="button"
          onClick={() => onRemove(file)}
          className="text-slate-400 hover:text-red-500"
          aria-label={`Remove ${file.name}`}
          data-testid={`provider-share-file-remove-${file.name}`}
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  );
};

const ProviderDocumentShareDialog = ({
  open,
  onClose,
  onShared,
  threadId,
  recipientLabel,
  actorRole,
  locale = 'en',
}) => {
  const [files, setFiles] = useState([]);
  const [category, setCategory] = useState('');
  const [notes, setNotes] = useState('');
  const [consented, setConsented] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // P0-PROVIDER-DOC-SHARE-DECLARATION — the Provider/Clinic declaration is
  // canonical legal content resolved at runtime from the legal authority
  // (journey `messaging_provider_document_share` → legal_policy_bindings →
  // platform_content_versions). This component owns NO legal wording.
  const [declaration, setDeclaration] = useState({ loading: true, error: null, item: null });
  const { t } = useTranslation();
  const inputRef = useRef(null);
  // P0-ATTACHMENT-THREAD-SAFETY-01 — the thread this draft belongs to.
  // Submission is refused if it ever differs from the active `threadId`.
  const draftThreadRef = useRef(null);

  // P0-ATTACHMENT-THREAD-SAFETY-01 — attachment draft state must NEVER
  // cross a thread boundary. `open={false}` only skips rendering (the
  // component stays mounted), so every lifecycle boundary that could
  // carry a draft between patients clears it here: modal close/cancel,
  // modal (re)open, and a change of the active thread while open.
  useEffect(() => {
    setFiles([]);
    setCategory('');
    setNotes('');
    setConsented(false);
    if (inputRef.current) inputRef.current.value = '';
    draftThreadRef.current = open ? threadId || null : null;
  }, [open, threadId]);

  // Resolve the CURRENT canonical declaration whenever the dialog opens
  // (and whenever actor role or locale changes). Acceptance is always
  // re-collected for the resolved declaration — it is never inherited
  // across opens or threads.
  useEffect(() => {
    if (!open) {
      setDeclaration({ loading: true, error: null, item: null });
      return undefined;
    }
    let cancelled = false;
    setDeclaration({ loading: true, error: null, item: null });
    const params = new URLSearchParams({ locale: locale || 'en' });
    if (actorRole) params.append('role', actorRole);
    api.get(`/legal/journey/${PROVIDER_DOC_SHARE_JOURNEY}/requirements?${params.toString()}`)
      .then((res) => {
        if (cancelled) return;
        const item = (res.data?.requirements || []).find((r) => r.required)
          || (res.data?.requirements || [])[0] || null;
        setDeclaration({
          loading: false,
          error: item ? null : 'declaration_unavailable',
          item,
        });
      })
      .catch(() => {
        if (!cancelled) {
          // FAIL CLOSED — never fall back to locally held legal wording.
          setDeclaration({ loading: false, error: 'declaration_unavailable', item: null });
        }
      });
    return () => { cancelled = true; };
  }, [open, threadId, actorRole, locale]);

  const totalSize = useMemo(
    () => files.reduce((acc, f) => acc + (f.size || 0), 0),
    [files]
  );
  const overLimit = totalSize > MAX_TOTAL_MB * 1024 * 1024;

  const canSubmit = useMemo(() => {
    if (!threadId) return false;
    if (!files.length) return false;
    if (!category) return false;
    // Blocking legal gate: the current canonical declaration must be
    // resolved AND explicitly accepted.
    if (!declaration.item) return false;
    if (!consented) return false;
    if (overLimit) return false;
    return !submitting;
  }, [threadId, files, category, consented, overLimit, submitting, declaration.item]);

  const handleFiles = (list) => {
    if (!list) return;
    const incoming = Array.from(list).slice(0, MAX_FILES - files.length);
    setFiles((prev) => [...prev, ...incoming]);
  };

  const handleRemove = (file) => {
    setFiles((prev) => prev.filter((f) => f !== file));
  };

  const handleShare = async () => {
    if (!canSubmit) return;
    // P0-ATTACHMENT-THREAD-SAFETY-01 — defence in depth: a draft may only
    // be submitted to the very thread it was created for. The canonical
    // target stays `threadId` (the active, authorized thread); we never
    // retarget a stale draft onto another patient.
    if (draftThreadRef.current && draftThreadRef.current !== threadId) {
      setFiles([]); setCategory(''); setNotes(''); setConsented(false);
      if (inputRef.current) inputRef.current.value = '';
      draftThreadRef.current = threadId || null;
      toast.error(t('provider.documentShare.toast.cancelledTitle'), {
        description: t('provider.documentShare.toast.cancelledBody'),
      });
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append('files', f));
      fd.append('thread_id', threadId);
      fd.append('document_category', category);
      if (notes) fd.append('notes', notes);
      // Canonical declaration evidence. The backend re-resolves the
      // authoritative version/locale/hash itself — we only state WHICH
      // declaration was presented and that it was explicitly accepted.
      fd.append('declaration_content_key', declaration.item.content_key);
      fd.append('declaration_accepted', 'true');
      fd.append('declaration_locale', declaration.item.locale || locale || 'en');
      const res = await api.post('/clinic/thread-attachments', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data?.success) {
        toast.success(t('provider.documentShare.toast.sharedTitle'), {
          description: `${res.data.count} file(s) sent securely.`,
        });
        onShared && onShared({
          count: res.data.count || files.length,
          attachment_ids: (res.data.files || []).map((f) => f.attachment_id).filter(Boolean),
        });
        // reset state and close
        setFiles([]); setCategory(''); setNotes(''); setConsented(false);
        if (inputRef.current) inputRef.current.value = '';
        draftThreadRef.current = null;
        onClose && onClose();
      } else {
        toast.error(t('provider.documentShare.toast.failedTitle'), {
          description: res.data?.detail || res.data?.error
            || t('provider.documentShare.toast.failedBody'),
        });
      }
    } catch (err) {
      toast.error(t('provider.documentShare.toast.failedTitle'), {
        description: err?.response?.data?.detail || err?.message
          || t('provider.documentShare.toast.networkError'),
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  // P0-ATTACHMENT-MODAL-LAYERING-01 — the dialog used to render inside the
  // composer, whose `sticky` container forms its own stacking context, so
  // sibling messaging surfaces painted above/through the modal. Portalling
  // the SAME markup to `document.body` removes every ancestor stacking
  // context; no second modal implementation is introduced and the existing
  // backdrop/z-index contract (z-[80], above the z-[70] file viewer) is kept.
  const dialog = (
    <div
      className="fixed inset-0 bg-black/60 z-[80] flex items-center justify-center p-4"
      data-testid="provider-share-dialog"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl max-w-md w-full overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b flex items-start gap-3">
          <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
            <ShieldCheck className="h-5 w-5 text-blue-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-slate-900" data-testid="provider-share-title">
              {t('provider.documentShare.title')}
            </h3>
            <p className="text-[11px] text-slate-500 mt-0.5">
              {t('provider.documentShare.subtitle')}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700"
            aria-label={t('provider.documentShare.close')}
            data-testid="provider-share-close-btn"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Recipient banner */}
        <div className="px-5 py-3 bg-slate-50 border-b flex items-center gap-2" data-testid="provider-share-recipient">
          <User className="h-4 w-4 text-slate-500" />
          <div className="text-xs">
            <div className="text-slate-500">{t('provider.documentShare.to')}</div>
            <div className="text-slate-900 font-medium" data-testid="provider-share-recipient-label">
              {recipientLabel || t('provider.messaging.patient')}
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4">
          {/* File picker */}
          <div>
            <label className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">
              {t('provider.documentShare.documents')}
            </label>
            <input
              ref={inputRef}
              type="file"
              multiple
              accept="application/pdf,image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={(e) => handleFiles(e.target.files)}
              data-testid="provider-share-file-input"
            />
            {files.length === 0 ? (
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                className="mt-1.5 w-full border-2 border-dashed border-slate-300 hover:border-blue-400 rounded-lg px-4 py-6 text-sm text-slate-500 hover:text-blue-700 transition flex flex-col items-center gap-2"
                data-testid="provider-share-pick-files-btn"
              >
                <Upload className="h-5 w-5" />
                Choose files (PDF or image, max {MAX_FILES})
              </button>
            ) : (
              <div className="mt-1.5 space-y-1.5">
                <div className="flex flex-wrap gap-1.5">
                  {files.map((f) => (
                    <FilePreview key={f.name} file={f} onRemove={handleRemove} />
                  ))}
                </div>
                {files.length < MAX_FILES && (
                  <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    className="text-[11px] text-blue-600 hover:text-blue-800"
                    data-testid="provider-share-add-more-btn"
                  >
                    {t('provider.documentShare.addAnother')}
                  </button>
                )}
                {overLimit && (
                  <div className="text-[11px] text-red-600 flex items-center gap-1" data-testid="provider-share-too-large">
                    <AlertCircle className="h-3 w-3" /> {t('provider.documentShare.tooLarge', { mb: MAX_TOTAL_MB })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Category */}
          <div>
            <label className="text-[11px] uppercase tracking-wide text-slate-500 font-medium" htmlFor="provider-share-category-select">
              {t('provider.documentShare.documentType')}
            </label>
            <select
              id="provider-share-category-select"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="mt-1.5 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white"
              data-testid="provider-share-category-select"
            >
              <option value="">{t('provider.documentShare.selectType')}</option>
              {CATEGORY_VALUES.map((value) => (
                <option key={value} value={value}>
                  {t(`provider.documentShare.categories.${value}`)}
                </option>
              ))}
            </select>
          </div>

          {/* Optional notes */}
          <div>
            <label className="text-[11px] uppercase tracking-wide text-slate-500 font-medium" htmlFor="provider-share-notes">
              {t('provider.documentShare.note')}
            </label>
            <textarea
              id="provider-share-notes"
              value={notes}
              maxLength={300}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder={t('provider.documentShare.notePlaceholder')}
              className="mt-1.5 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              data-testid="provider-share-notes"
            />
          </div>

          {/* Canonical Provider/Clinic declaration — content and wording
              come from the legal authority, never from this component. */}
          {declaration.loading && (
            <div className="text-xs text-slate-500 py-2" data-testid="provider-share-declaration-loading">
              <Loader2 className="h-3 w-3 animate-spin inline mr-2" />
              {t('provider.documentShare.declarationLoading')}
            </div>
          )}
          {!declaration.loading && !declaration.item && (
            <div
              className="flex items-start gap-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2"
              data-testid="provider-share-declaration-unavailable"
            >
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>{t('provider.documentShare.declarationUnavailable')}</span>
            </div>
          )}
          {!declaration.loading && declaration.item && (
            <div className="border border-slate-200 rounded-lg" data-testid="provider-share-declaration">
              <div
                className="px-3 py-2 border-b bg-slate-50 text-xs font-semibold text-slate-800"
                data-testid="provider-share-declaration-title"
              >
                {declaration.item.title}
              </div>
              <div
                className="px-3 py-2 max-h-40 overflow-y-auto text-xs text-slate-600 leading-relaxed [&_ul]:list-disc [&_ul]:pl-4 [&_p]:mb-2"
                data-testid="provider-share-declaration-body"
              >
                <SafeHtml html={declaration.item.body_html} />
              </div>
              <label className="flex items-start gap-2 text-xs text-slate-700 cursor-pointer px-3 py-2 border-t">
                <input
                  type="checkbox"
                  checked={consented}
                  onChange={(e) => setConsented(e.target.checked)}
                  className="mt-0.5 h-4 w-4"
                  data-testid="provider-share-consent-checkbox"
                />
                <span data-testid="provider-share-declaration-acceptance-label">
                  {declaration.item.acceptance_label || declaration.item.title}
                </span>
              </label>
              <div className="px-3 pb-2 text-[10px] text-slate-400" data-testid="provider-share-declaration-version">
                {declaration.item.content_key} · v{declaration.item.version} · {declaration.item.locale}
                {declaration.item.is_locale_fallback
                  ? ` (${t('provider.documentShare.localeFallback')})` : ''}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 bg-slate-50 border-t">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={submitting}
            data-testid="provider-share-cancel-btn"
          >
            {t('provider.documentShare.cancel')}
          </Button>
          <Button
            onClick={handleShare}
            disabled={!canSubmit}
            className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
            data-testid="provider-share-submit-btn"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
            {t('provider.documentShare.submit')}
          </Button>
        </div>
      </div>
    </div>
  );

  return typeof document !== 'undefined' && document.body
    ? createPortal(dialog, document.body)
    : dialog;
};

export default ProviderDocumentShareDialog;
